/**
 * React hook for confetti effects
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import { CONFETTI_COLORS } from '../utils/colors';
import type { UseConfettiOptions, UseConfettiReturn } from '../types/wheel';

export function useConfetti(options: UseConfettiOptions = {}): UseConfettiReturn {
  const { enabled = true, colors = CONFETTI_COLORS } = options;

  const [isEnabled, setIsEnabled] = useState(enabled);
  const confettiRef = useRef<confetti.CreateTypes | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Update enabled state when prop changes
  useEffect(() => {
    setIsEnabled(enabled);
  }, [enabled]);

  // Fire confetti effect
  const fire = useCallback(() => {
    if (!isEnabled || typeof window === 'undefined') return;

    let burstCount = 0;
    const maxBursts = 4;
    let lastBurstTime = 0;
    const burstInterval = 250;

    const animate = (timestamp: number) => {
      if (burstCount >= maxBursts) return;

      if (timestamp - lastBurstTime > burstInterval) {
        lastBurstTime = timestamp;
        burstCount++;

        confetti({
          particleCount: 80,
          spread: 360,
          startVelocity: 5,
          decay: 0.98,
          ticks: 200,
          shapes: ['circle'],
          colors,
          origin: {
            x: Math.random(),
            y: Math.random() - 0.3,
          },
        });
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [isEnabled, colors]);

  // Reset/cleanup confetti
  const reset = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (confettiRef.current) {
      confettiRef.current.reset();
      confettiRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  return {
    fire,
    reset,
  };
}
